/*
 * Lola Health's clinic picker: the dispatch venue finder in select mode on the product page, and
 * the cart-line properties a chosen venue becomes. lola-be reads those properties when the order
 * arrives (LineItemClinic), so their names are a contract: the twelve `_clinic_*` keys it has
 * always read, plus the dispatch venue id and the partner's own id for the venue.
 *
 * Also loads dispatch's embed.js for any browse-mode finder on the page (find-a-clinic), so the
 * book host is named in one place.
 */
(function (window, document) {
  'use strict';

  if (window.LolaClinicPicker) return;

  // Where dispatch serves the finder, and which dispatch client this storefront is: the theme
  // settings under Clinic finder, handed over on this script's own tag. The client id is public;
  // dispatch frames the finder only on origins registered for that client.
  var config = (document.currentScript && document.currentScript.dataset) || {};
  var DISPATCH_BOOK_ORIGIN = (config.bookOrigin || '').replace(/\/+$/, '');
  var DISPATCH_CLIENT_ID = config.client || '';

  var VENUE_SELECTED = 'lola-dispatch:venue-selected';
  var VENUE_CLEARED = 'lola-dispatch:venue-cleared';
  var ESCAPE = 'lola-dispatch:escape';

  // dispatch's venue network is UK-only and its payload carries no country.
  var COUNTRY = 'GB';

  // Partners whose venues lola-be can route, keyed by dispatch partner slug, valued by lola-be
  // clinic source. Any other partner's venue is refused, never guessed at; adding one needs its
  // routing in lola-be first.
  var PARTNER_SOURCES = Object.freeze({ tuli: 'tuli' });

  // What lola-be refuses a clinic without (ClinicModel.validate), by source, plus the venue id
  // every line now carries. lola-be builds a Tuli booking link from the partner's venue id.
  var REQUIRED_FIELDS = {
    lola: ['id', 'name', 'addressLine1', 'postcode', 'email', 'phone'],
    tuli: ['id', 'name', 'addressLine1', 'externalVenueId'],
  };

  var ACCESS_LABELS = {
    WALK_IN: 'Walk-in',
    APPOINTMENT: 'Appointment',
    WALK_IN_OR_APPOINTMENT: 'Walk-in or appointment',
  };

  function text(value) {
    return value === null || value === undefined ? '' : String(value);
  }

  function sourceOf(venue) {
    var operator = venue.operator;
    var type = operator ? operator.type : null;
    if (type === 'OWN' || type === 'GROUP') return 'lola';
    if (type !== 'PARTNER') {
      throw new Error('Venue ' + text(venue.id) + ' has no recognised operator (got "' + text(type) + '")');
    }
    var slug = text(venue.partnerSlug);
    if (!Object.prototype.hasOwnProperty.call(PARTNER_SOURCES, slug)) {
      throw new Error('Venue ' + text(venue.id) + ' is run by partner "' + slug + '", which Lola Health cannot route');
    }
    return PARTNER_SOURCES[slug];
  }

  function toClinicProperties(venue) {
    var source = sourceOf(venue);
    REQUIRED_FIELDS[source].forEach(function (field) {
      if (text(venue[field]).trim() === '') {
        throw new Error('Venue ' + text(venue.id) + ' has no ' + field + ', which a ' + source + ' clinic needs');
      }
    });

    return {
      'Selected Clinic': text(venue.name) + ' (' + text(venue.addressLine1) + ')',
      'Clinic Access Type': ACCESS_LABELS[venue.accessType] || '',
      _clinic_name: text(venue.name),
      _clinic_address1: text(venue.addressLine1),
      _clinic_address2: '',
      _clinic_city: text(venue.city),
      _clinic_postcode: text(venue.postcode),
      _clinic_country: COUNTRY,
      _clinic_source: source,
      _clinic_email: text(venue.email),
      _clinic_phone: text(venue.phone),
      _clinic_walkin: venue.walkIn === true ? 'true' : 'false',
      _clinic_website: text(venue.website),
      _clinic_booking_url: '',
      _clinic_venue_id: text(venue.id),
      _clinic_external_id: text(venue.externalVenueId),
    };
  }

  var embedRequested = false;

  // Loads dispatch's loader once. On the product page it is requested only when the picker first
  // opens, after the finder element is in the document, so a page view that never opens the
  // picker loads no map.
  function loadEmbed() {
    if (embedRequested) return;
    if (!DISPATCH_BOOK_ORIGIN) throw new Error('clinic-picker.js: the dispatch_book_origin theme setting is empty, so the clinic finder cannot load');
    embedRequested = true;
    var script = document.createElement('script');
    script.src = DISPATCH_BOOK_ORIGIN + '/embed.js';
    script.async = true;
    // dispatch's loader trusts only its own tag when the browser gives it no currentScript.
    script.setAttribute('data-lola-dispatch-loader', '');
    // Blocked or offline: say so in the picker, and let the next open try again.
    script.onerror = function () {
      embedRequested = false;
      if (script.parentNode) script.parentNode.removeChild(script);
      var dialog = document.body.querySelectorAll('[data-clinic-picker-dialog]')[0];
      if (dialog) showLoadError(dialog, true);
    };
    document.head.appendChild(script);
  }

  // Each `properties[...]` input in buy-buttons' #clinic_inputs, with a `hidden_` twin that
  // product-info.js copies back when the clinic variant is chosen again.
  function writeProperties(properties) {
    var container = document.getElementById('clinic_inputs');
    if (!container) return;
    Array.prototype.forEach.call(container.querySelectorAll('input'), function (input) {
      var match = /^properties\[(.+)\]$/.exec(input.getAttribute('name') || '');
      if (!match) return;
      var value = properties ? text(properties[match[1]]) : '';
      input.value = value;
      var twin = document.getElementById('hidden_' + input.id);
      if (twin) twin.value = value;
    });
  }

  function setText(id, value) {
    var element = document.getElementById(id);
    if (element) element.textContent = value;
  }

  // The add-to-cart gate in product-info.js reads #selected_clinic_name; this keeps the button
  // in step with it while the clinic variant is on screen. The opener does not choose that
  // variant, so another (possibly sold-out) one's button is left alone, and product-info.js opens
  // the clinic variant's once it is chosen.
  function setSubmitEnabled(enabled) {
    var form = document.querySelector('product-form');
    var sectionId = form && form.dataset ? form.dataset.sectionId : null;
    var buttons = [
      sectionId ? document.getElementById('ProductSubmitButton-' + sectionId) : null,
      document.querySelector('.product-form__buttons .button.add-to-cart-button[data-micromodal-trigger]'),
    ];
    var strings = window.variantStrings;
    var clinicButtons = buttons.filter(function (button) {
      return button && button.hasAttribute('data-clinic');
    });
    if (!clinicButtons.length) return;
    // The add-to-basket button main-product.liquid builds under the add-ons on mobile follows the
    // main one through an observer bound to the button it first saw; set it here too.
    var addonsButton = document.querySelector('.secondary-add-to-basket button');
    if (addonsButton) clinicButtons.push(addonsButton);
    var preorderLabel = clinicButtons[0].dataset ? clinicButtons[0].dataset.preorderLabel : null;
    clinicButtons.forEach(function (button) {
      if (enabled) button.removeAttribute('disabled');
      else button.setAttribute('disabled', 'disabled');
      var label = button.querySelector('span');
      if (!label || !strings) return;
      label.textContent = enabled ? preorderLabel || strings.addToCart : strings.chooseClinic;
    });
  }

  function showError(dialog, visible) {
    var error = dialog.querySelector('[data-clinic-picker-error]');
    if (error) error.hidden = !visible;
  }

  function showLoadError(dialog, visible) {
    var error = dialog.querySelector('[data-clinic-picker-load-error]');
    if (!error) return;
    if (visible) error.removeAttribute('hidden');
    else error.setAttribute('hidden', '');
  }

  function closeDialog(dialog) {
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    document.body.style.overflow = '';
  }

  function onVenueSelected(event) {
    var dialog = event.target && event.target.closest ? event.target.closest('[data-clinic-picker-dialog]') : null;
    if (!dialog) return;

    var properties;
    try {
      properties = toClinicProperties(event.detail || {});
    } catch (error) {
      writeProperties(null);
      setText('selected_clinic_name', '');
      setText('selected_clinic_access_type', '');
      setSubmitEnabled(false);
      showError(dialog, true);
      throw error;
    }

    showError(dialog, false);
    writeProperties(properties);
    setText('selected_clinic_name', 'Clinic selected: ' + properties['Selected Clinic']);
    setText('selected_clinic_access_type', properties['Clinic Access Type'] ? 'Access Type: ' + properties['Clinic Access Type'] : '');
    setSubmitEnabled(true);
    closeDialog(dialog);
  }

  // Clear on the finder's Chosen bar: the clinic goes back to unchosen, add-to-cart waits for one,
  // and the dialog stays open for the next choice.
  function onVenueCleared(event) {
    var dialog = event.target && event.target.closest ? event.target.closest('[data-clinic-picker-dialog]') : null;
    if (!dialog) return;
    showError(dialog, false);
    writeProperties(null);
    setText('selected_clinic_name', '');
    setText('selected_clinic_access_type', '');
    setSubmitEnabled(false);
  }

  // The dialog lives in a <template> in the variant snippet and is cloned to <body> on first use,
  // so it survives product-form.js re-rendering the product section after an add to cart, and
  // the loader finds the finder element already in the document when it runs.
  function pickerDialog() {
    var existing = document.body.querySelectorAll('[data-clinic-picker-dialog]')[0];
    if (existing) return existing;
    var template = document.querySelectorAll('[data-clinic-picker-template]')[0];
    if (!template) return null;
    document.body.appendChild(template.content.cloneNode(true));
    return document.body.querySelectorAll('[data-clinic-picker-dialog]')[0] || null;
  }

  function openPicker() {
    var dialog = pickerDialog();
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    document.body.style.overflow = 'hidden';
    showLoadError(dialog, false);
    var finder = dialog.querySelector('[data-lola-dispatch-venues]');
    if (finder && !finder.getAttribute('data-client')) {
      if (!DISPATCH_CLIENT_ID) {
        showLoadError(dialog, true);
        throw new Error('clinic-picker.js: the dispatch_client_id theme setting is empty, so the clinic finder cannot load');
      }
      finder.setAttribute('data-client', DISPATCH_CLIENT_ID);
      // This page handles lola-dispatch:venue-cleared, so the finder may offer Clear.
      finder.setAttribute('data-clearable', 'true');
      // The dialog gives the finder a fixed height; the finder scrolls its own list inside it.
      finder.setAttribute('data-height', 'fill');
    }
    loadEmbed();
  }

  function trigger(event, selector) {
    return event.target && event.target.closest ? event.target.closest(selector) : null;
  }

  // The opener sits inside the variant's <label>; preventDefault keeps a click on it from also
  // choosing the radio.
  document.addEventListener('click', function (event) {
    if (trigger(event, '[data-clinic-picker-open]')) {
      event.preventDefault();
      openPicker();
      return;
    }
    var close = trigger(event, '[data-clinic-picker-close]');
    if (close) {
      event.preventDefault();
      closeDialog(close.closest('[data-clinic-picker-dialog]'));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!trigger(event, '[data-clinic-picker-open]')) return;
    event.preventDefault();
    openPicker();
  });

  // Escape closes a modal dialog without a click; 'close' does not bubble, so listen in capture.
  document.addEventListener(
    'close',
    function (event) {
      if (trigger(event, '[data-clinic-picker-dialog]')) document.body.style.overflow = '';
    },
    true
  );

  // Escape pressed inside the finder's frame: dispatch's loader re-raises it on the finder element.
  document.addEventListener(
    ESCAPE,
    function (event) {
      var dialog = trigger(event, '[data-clinic-picker-dialog]');
      if (dialog) closeDialog(dialog);
    },
    true
  );

  // Captured at the document so the selection is seen whether or not the loader's event bubbles.
  document.addEventListener(VENUE_SELECTED, onVenueSelected, true);
  document.addEventListener(VENUE_CLEARED, onVenueCleared, true);

  var hasBrowseFinder = Array.prototype.some.call(document.querySelectorAll('[data-lola-dispatch-venues]'), function (element) {
    return element.getAttribute('data-mode') === 'browse';
  });
  if (hasBrowseFinder) loadEmbed();

  window.LolaClinicPicker = {
    PARTNER_SOURCES: PARTNER_SOURCES,
    toClinicProperties: toClinicProperties,
    loadEmbed: loadEmbed,
    open: openPicker,
  };
})(window, document);
